/**
 * BattleshipEngine — Core game logic for Battleship
 *
 * Manages game state (two 10×10 grids per player — Ocean Grid and Tracking Grid),
 * validates ship placements, processes attacks with hit/miss/sunk detection,
 * enforces turn management, and detects win conditions.
 *
 * Grid convention: grid[row][col], columns A–J (index 0–9), rows 1–10 (index 0–9)
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 2.2–2.7, 3.1–3.5, 4.1–4.6, 8.4, 8.5
 */

import {
  OceanGrid,
  TrackingGrid,
  OceanCellState,
  TrackingCellState,
  GamePhase,
  PlayerSide,
  ShipType,
  ShipPlacement,
  PlacementResult,
  PlacementValidation,
  ShipStatus,
  FleetStatus,
  BattleshipGameState,
  BattleshipGameStatus,
  AttackRecord,
  AttackResult,
  AttackValidation,
  Coordinate,
  Orientation,
  SHIP_SIZES,
  GRID_ROWS,
  GRID_COLS,
  getShipCells,
  isValidCoordinate,
  coordinatesEqual,
} from './types';

// ============================================================================
// Helper: create a 10×10 grid filled with a given value
// ============================================================================

function createGrid<T>(fill: T): T[][] {
  return Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => fill)
  );
}

// ============================================================================
// All five ship types in standard order (descending size for auto-place)
// ============================================================================

const ALL_SHIP_TYPES: ShipType[] = [
  'carrier',
  'battleship',
  'cruiser',
  'submarine',
  'destroyer',
];

// ============================================================================
// BattleshipEngine
// ============================================================================

export class BattleshipEngine {
  // Grids
  private player1OceanGrid!: OceanGrid;
  private player2OceanGrid!: OceanGrid;
  private player1TrackingGrid!: TrackingGrid;
  private player2TrackingGrid!: TrackingGrid;

  // Fleet placements
  private player1Fleet: ShipPlacement[] = [];
  private player2Fleet: ShipPlacement[] = [];

  // Game metadata
  private gamePhase!: GamePhase;
  private currentTurn!: PlayerSide;
  private attackHistory!: AttackRecord[];
  private gameStatus!: BattleshipGameStatus;

  // Placement readiness
  private player1Ready!: boolean;
  private player2Ready!: boolean;

  constructor() {
    this.initializeGame();
  }

  // ==========================================================================
  // Game Initialization (Task 2.1)
  // ==========================================================================

  /**
   * Initialize a fresh game: empty grids, placement phase, player1 starts.
   * Requirements: 1.1, 1.2, 1.4, 1.5
   */
  initializeGame(): void {
    this.player1OceanGrid = createGrid<OceanCellState>('empty');
    this.player2OceanGrid = createGrid<OceanCellState>('empty');
    this.player1TrackingGrid = createGrid<TrackingCellState>('unknown');
    this.player2TrackingGrid = createGrid<TrackingCellState>('unknown');

    this.player1Fleet = [];
    this.player2Fleet = [];

    this.gamePhase = 'placement';
    this.currentTurn = 'player1';
    this.attackHistory = [];
    this.gameStatus = { type: 'placement' };

    this.player1Ready = false;
    this.player2Ready = false;
  }

  // ==========================================================================
  // State Queries (Task 2.1)
  // ==========================================================================

  getGamePhase(): GamePhase {
    return this.gamePhase;
  }

  getCurrentTurn(): PlayerSide {
    return this.currentTurn;
  }

  getGameStatus(): BattleshipGameStatus {
    return this.gameStatus;
  }

  getAttackHistory(): AttackRecord[] {
    return [...this.attackHistory];
  }

  getOceanGrid(player: PlayerSide): OceanGrid {
    const grid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;
    return grid.map(row => [...row]);
  }

  getTrackingGrid(player: PlayerSide): TrackingGrid {
    const grid = player === 'player1' ? this.player1TrackingGrid : this.player2TrackingGrid;
    return grid.map(row => [...row]);
  }

  /**
   * Returns the full game state snapshot.
   */
  getGameState(): BattleshipGameState {
    return {
      player1OceanGrid: this.getOceanGrid('player1'),
      player2OceanGrid: this.getOceanGrid('player2'),
      player1TrackingGrid: this.getTrackingGrid('player1'),
      player2TrackingGrid: this.getTrackingGrid('player2'),
      player1Fleet: [...this.player1Fleet],
      player2Fleet: [...this.player2Fleet],
      player1FleetStatus: this.getFleetStatus('player1'),
      player2FleetStatus: this.getFleetStatus('player2'),
      gamePhase: this.gamePhase,
      currentTurn: this.currentTurn,
      attackHistory: this.getAttackHistory(),
      gameStatus: this.gameStatus,
      player1Ready: this.player1Ready,
      player2Ready: this.player2Ready,
    };
  }

  // ==========================================================================
  // Fleet Status (Task 2.1)
  // ==========================================================================

  /**
   * Returns the status of each ship in a player's fleet.
   * Requirements: 8.4, 8.5
   */
  getFleetStatus(player: PlayerSide): FleetStatus {
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    const trackingGridOfOpponent =
      player === 'player1' ? this.player2TrackingGrid : this.player1TrackingGrid;

    const ships: ShipStatus[] = fleet.map(placement => {
      const cells = getShipCells(placement);
      const oceanGrid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;

      // A cell is hit if the opponent's tracking grid shows 'hit' or 'sunk' at that position,
      // OR we can check the ocean grid for hit markers. Since we don't store hit state on ocean
      // grid cells (they stay 'ship'), we check the opponent's tracking grid.
      // Actually, the opponent's tracking grid tracks attacks against THIS player.
      // The opponent of 'player1' is 'player2', so player2's tracking grid tracks attacks on player1.
      const opponentTrackingGrid =
        player === 'player1' ? this.player2TrackingGrid : this.player1TrackingGrid;

      const hitCells = cells.filter(
        cell =>
          opponentTrackingGrid[cell.row][cell.col] === 'hit' ||
          opponentTrackingGrid[cell.row][cell.col] === 'sunk'
      );

      const isSunk = hitCells.length === cells.length;

      return {
        shipType: placement.shipType,
        size: SHIP_SIZES[placement.shipType],
        placement,
        cells,
        hitCells,
        isSunk,
      };
    });

    const shipsAfloat = ships.filter(s => !s.isSunk).length;
    const shipsSunk = ships.filter(s => s.isSunk).length;

    return {
      ships,
      totalShips: fleet.length,
      shipsAfloat,
      shipsSunk,
    };
  }

  /**
   * Returns the count of non-sunk ships for a player.
   */
  getRemainingShipCount(player: PlayerSide): number {
    const status = this.getFleetStatus(player);
    return status.shipsAfloat;
  }

  // ==========================================================================
  // Ship Placement Validation (Task 2.2)
  // ==========================================================================

  /**
   * Validates whether a ship placement is legal.
   * Checks: within bounds, no overlap, horizontal/vertical only, not already placed.
   * Requirements: 2.4, 2.5, 4.1, 4.2, 4.3, 4.5, 4.6
   */
  isPlacementValid(player: PlayerSide, placement: ShipPlacement): PlacementValidation {
    // Check orientation is horizontal or vertical (type system enforces this, but be safe)
    if (placement.orientation !== 'horizontal' && placement.orientation !== 'vertical') {
      return { valid: false, reason: 'invalid_orientation' };
    }

    // Check if ship type is already placed
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    if (fleet.some(p => p.shipType === placement.shipType)) {
      return { valid: false, reason: 'already_placed' };
    }

    // Get all cells the ship would occupy
    const cells = getShipCells(placement);

    // Check all cells are within bounds
    for (const cell of cells) {
      if (!isValidCoordinate(cell)) {
        return { valid: false, reason: 'out_of_bounds' };
      }
    }

    // Check no overlap with existing ships
    const oceanGrid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;
    for (const cell of cells) {
      if (oceanGrid[cell.row][cell.col] === 'ship') {
        return { valid: false, reason: 'overlap' };
      }
    }

    return { valid: true };
  }

  // ==========================================================================
  // Ship Placement (Task 2.2)
  // ==========================================================================

  /**
   * Places a ship on the player's ocean grid after validation.
   * Requirements: 2.2, 2.3, 2.4, 2.5
   */
  placeShip(player: PlayerSide, placement: ShipPlacement): PlacementResult {
    const validation = this.isPlacementValid(player, placement);
    if (!validation.valid) {
      return { success: false, error: validation.reason };
    }

    const cells = getShipCells(placement);
    const oceanGrid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;

    // Mark cells on ocean grid
    for (const cell of cells) {
      oceanGrid[cell.row][cell.col] = 'ship';
    }

    // Add to fleet
    fleet.push(placement);

    return {
      success: true,
      placements: [...fleet],
    };
  }

  /**
   * Removes a placed ship from the player's ocean grid.
   * Returns true if the ship was found and removed, false otherwise.
   * Requirements: 2.7
   */
  removeShip(player: PlayerSide, shipType: ShipType): boolean {
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    const oceanGrid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;

    const index = fleet.findIndex(p => p.shipType === shipType);
    if (index === -1) {
      return false;
    }

    // Clear cells on ocean grid
    const cells = getShipCells(fleet[index]);
    for (const cell of cells) {
      oceanGrid[cell.row][cell.col] = 'empty';
    }

    // Remove from fleet
    fleet.splice(index, 1);
    return true;
  }

  /**
   * Removes all placed ships for a player.
   */
  clearAllShips(player: PlayerSide): void {
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    const oceanGrid = player === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;

    // Clear all ship cells on ocean grid
    for (const placement of fleet) {
      const cells = getShipCells(placement);
      for (const cell of cells) {
        oceanGrid[cell.row][cell.col] = 'empty';
      }
    }

    // Clear fleet
    fleet.length = 0;
  }

  /**
   * Returns true when all 5 ships are placed for a player.
   * Requirements: 4.4
   */
  isFleetComplete(player: PlayerSide): boolean {
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    return fleet.length === ALL_SHIP_TYPES.length;
  }

  /**
   * Returns the current ship placements for a player.
   */
  getPlacedShips(player: PlayerSide): ShipPlacement[] {
    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    return [...fleet];
  }

  // ==========================================================================
  // Auto-Place (Task 2.3)
  // ==========================================================================

  /**
   * Automatically places all 5 ships in valid random positions.
   * Clears any existing placements first.
   * Places ships in descending size order (Carrier first, Destroyer last).
   * Must always succeed and complete within 500ms.
   * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5
   */
  autoPlaceShips(player: PlayerSide): PlacementResult {
    // Clear existing placements
    this.clearAllShips(player);

    const orientations: Orientation[] = ['horizontal', 'vertical'];

    // Place ships in descending size order
    for (const shipType of ALL_SHIP_TYPES) {
      const size = SHIP_SIZES[shipType];
      let placed = false;

      // Try random placements until one works
      // With a 10×10 grid and max 17 cells occupied, this converges quickly
      for (let attempt = 0; attempt < 1000; attempt++) {
        const orientation = orientations[Math.floor(Math.random() * 2)];
        const maxRow = orientation === 'vertical' ? GRID_ROWS - size : GRID_ROWS - 1;
        const maxCol = orientation === 'horizontal' ? GRID_COLS - size : GRID_COLS - 1;

        const row = Math.floor(Math.random() * (maxRow + 1));
        const col = Math.floor(Math.random() * (maxCol + 1));

        const placement: ShipPlacement = {
          shipType,
          startCell: { row, col },
          orientation,
        };

        const validation = this.isPlacementValid(player, placement);
        if (validation.valid) {
          this.placeShip(player, placement);
          placed = true;
          break;
        }
      }

      // This should never happen with a 10×10 grid and 5 ships
      if (!placed) {
        // Fallback: clear and retry the entire placement
        this.clearAllShips(player);
        return this.autoPlaceShips(player);
      }
    }

    const fleet = player === 'player1' ? this.player1Fleet : this.player2Fleet;
    return {
      success: true,
      placements: [...fleet],
    };
  }

  // ==========================================================================
  // Phase Transition (Task 3.1)
  // ==========================================================================

  /**
   * Transitions from placement phase to attack phase.
   * Both players must have complete fleets (all 5 ships placed).
   * Requirements: 6.1
   */
  transitionToAttackPhase(): boolean {
    if (this.gamePhase !== 'placement') {
      return false;
    }

    if (!this.isFleetComplete('player1') || !this.isFleetComplete('player2')) {
      return false;
    }

    this.gamePhase = 'attack';
    this.gameStatus = { type: 'active' };
    this.currentTurn = 'player1';
    this.player1Ready = true;
    this.player2Ready = true;

    return true;
  }

  // ==========================================================================
  // Attack Validation (Task 3.1)
  // ==========================================================================

  /**
   * Validates whether an attack is legal.
   * Checks: coordinate within bounds, cell not already attacked, correct turn,
   * game in attack phase, game not over.
   * Requirements: 6.3, 6.4, 6.5, 10.4
   */
  isValidAttack(attacker: PlayerSide, coordinate: Coordinate): AttackValidation {
    // Check game is not over (completed phase)
    if (this.gamePhase === 'completed' || this.gameStatus.type === 'win' || this.gameStatus.type === 'resignation') {
      return { valid: false, reason: 'game_over' };
    }

    // Check game phase is attack
    if (this.gamePhase !== 'attack') {
      return { valid: false, reason: 'wrong_phase' };
    }

    // Check it is the attacker's turn
    if (this.currentTurn !== attacker) {
      return { valid: false, reason: 'not_your_turn' };
    }

    // Check coordinate within bounds
    if (!isValidCoordinate(coordinate)) {
      return { valid: false, reason: 'out_of_bounds' };
    }

    // Check cell not already attacked — check the attacker's tracking grid
    if (this.hasBeenAttacked(attacker === 'player1' ? 'player2' : 'player1', coordinate)) {
      return { valid: false, reason: 'already_attacked' };
    }

    return { valid: true };
  }

  /**
   * Checks if a cell on the player's Ocean Grid has been targeted.
   * We check the opponent's tracking grid for that cell.
   * Requirements: 6.4
   */
  hasBeenAttacked(player: PlayerSide, coordinate: Coordinate): boolean {
    // The opponent's tracking grid records attacks against this player
    const opponentTrackingGrid =
      player === 'player1' ? this.player2TrackingGrid : this.player1TrackingGrid;

    const cellState = opponentTrackingGrid[coordinate.row][coordinate.col];
    return cellState !== 'unknown';
  }

  // ==========================================================================
  // Attack Processing (Task 3.1)
  // ==========================================================================

  /**
   * Processes an attack: validates, checks against defender's Ocean Grid,
   * returns Hit/Miss/Sunk result, updates grids, records in history.
   * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 8.1, 8.2, 8.3
   */
  processAttack(attacker: PlayerSide, coordinate: Coordinate): AttackResult {
    // Validate the attack
    const validation = this.isValidAttack(attacker, coordinate);
    if (!validation.valid) {
      return {
        success: false,
        coordinate,
        result: 'miss',
        error: validation.reason,
        gameOver: false,
      };
    }

    const defender: PlayerSide = attacker === 'player1' ? 'player2' : 'player1';
    const defenderOceanGrid =
      defender === 'player1' ? this.player1OceanGrid : this.player2OceanGrid;
    const attackerTrackingGrid =
      attacker === 'player1' ? this.player1TrackingGrid : this.player2TrackingGrid;

    // Check what's at the coordinate on the defender's ocean grid
    const cellState = defenderOceanGrid[coordinate.row][coordinate.col];
    const isHit = cellState === 'ship';

    if (isHit) {
      // Mark as hit on attacker's tracking grid
      attackerTrackingGrid[coordinate.row][coordinate.col] = 'hit';

      // Check if this hit sinks a ship
      const sunkShip = this.checkForSunkShip(defender, coordinate, attackerTrackingGrid);

      if (sunkShip) {
        // Mark all cells of the sunk ship as 'sunk' on the tracking grid
        const defenderFleet = defender === 'player1' ? this.player1Fleet : this.player2Fleet;
        const sunkPlacement = defenderFleet.find(p => p.shipType === sunkShip);
        if (sunkPlacement) {
          const sunkCells = getShipCells(sunkPlacement);
          for (const cell of sunkCells) {
            attackerTrackingGrid[cell.row][cell.col] = 'sunk';
          }
        }

        // Record attack in history
        const moveNumber = this.attackHistory.length + 1;
        this.attackHistory.push({
          moveNumber,
          attacker,
          coordinate,
          result: 'sunk',
          sunkShip,
          timestamp: Date.now(),
        });

        // Check win condition (Task 3.2)
        const defenderFleetStatus = this.getFleetStatus(defender);
        if (defenderFleetStatus.shipsAfloat === 0) {
          this.gamePhase = 'completed';
          this.gameStatus = { type: 'win', winner: attacker };

          return {
            success: true,
            coordinate,
            result: 'sunk',
            sunkShip,
            gameOver: true,
            winner: attacker,
          };
        }

        // Switch turn (Task 3.2)
        this.currentTurn = defender;

        return {
          success: true,
          coordinate,
          result: 'sunk',
          sunkShip,
          gameOver: false,
        };
      }

      // Hit but not sunk
      const moveNumber = this.attackHistory.length + 1;
      this.attackHistory.push({
        moveNumber,
        attacker,
        coordinate,
        result: 'hit',
        timestamp: Date.now(),
      });

      // Switch turn (Task 3.2)
      this.currentTurn = defender;

      return {
        success: true,
        coordinate,
        result: 'hit',
        gameOver: false,
      };
    } else {
      // Miss
      attackerTrackingGrid[coordinate.row][coordinate.col] = 'miss';

      const moveNumber = this.attackHistory.length + 1;
      this.attackHistory.push({
        moveNumber,
        attacker,
        coordinate,
        result: 'miss',
        timestamp: Date.now(),
      });

      // Switch turn (Task 3.2)
      this.currentTurn = defender;

      return {
        success: true,
        coordinate,
        result: 'miss',
        gameOver: false,
      };
    }
  }

  /**
   * Checks if hitting a coordinate sinks a ship.
   * Returns the ship type if sunk, null otherwise.
   */
  private checkForSunkShip(
    defender: PlayerSide,
    hitCoordinate: Coordinate,
    attackerTrackingGrid: TrackingGrid
  ): ShipType | null {
    const defenderFleet = defender === 'player1' ? this.player1Fleet : this.player2Fleet;

    for (const placement of defenderFleet) {
      const cells = getShipCells(placement);

      // Check if the hit coordinate is part of this ship
      const isPartOfShip = cells.some(cell => coordinatesEqual(cell, hitCoordinate));
      if (!isPartOfShip) continue;

      // Check if all cells of this ship are now hit (including the current hit)
      const allHit = cells.every(cell => {
        if (coordinatesEqual(cell, hitCoordinate)) return true; // Just hit this one
        const state = attackerTrackingGrid[cell.row][cell.col];
        return state === 'hit' || state === 'sunk';
      });

      if (allHit) {
        return placement.shipType;
      }
    }

    return null;
  }

  // ==========================================================================
  // Win Condition and Game Over (Task 3.2)
  // ==========================================================================

  /**
   * Returns true if the game is over (a winner has been declared or resignation).
   * Requirements: 9.1, 9.2
   */
  isGameOver(): boolean {
    return this.gamePhase === 'completed';
  }

  /**
   * Returns the winner if the game is over, null otherwise.
   * Requirements: 9.1
   */
  getWinner(): PlayerSide | null {
    if (this.gameStatus.type === 'win') {
      return this.gameStatus.winner;
    }
    if (this.gameStatus.type === 'resignation') {
      return this.gameStatus.winner;
    }
    return null;
  }

  // ==========================================================================
  // Serialization / Deserialization (Task 4.1)
  // ==========================================================================

  /**
   * Serializes the complete game state to a JSON string.
   * Ship positions are stored as starting cell, orientation, and ship type.
   * Requirements: 5.1, 5.5
   */
  serialize(): string {
    return JSON.stringify(this.getGameState());
  }

  /**
   * Deserializes a JSON string into a BattleshipGameState, with validation.
   * Returns a descriptive error for invalid/malformed JSON.
   * Requirements: 5.2, 5.4
   */
  deserialize(json: string): BattleshipGameState | { error: string } {
    if (!json || json.trim() === '') {
      return { error: 'Invalid JSON: input is empty' };
    }

    let parsed: any;
    try {
      parsed = JSON.parse(json);
    } catch {
      return { error: 'Invalid JSON: unable to parse input' };
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: 'Invalid JSON: expected an object' };
    }

    // Validate required top-level fields
    const requiredFields: Array<keyof BattleshipGameState> = [
      'player1OceanGrid',
      'player2OceanGrid',
      'player1TrackingGrid',
      'player2TrackingGrid',
      'player1Fleet',
      'player2Fleet',
      'player1FleetStatus',
      'player2FleetStatus',
      'gamePhase',
      'currentTurn',
      'attackHistory',
      'gameStatus',
      'player1Ready',
      'player2Ready',
    ];

    for (const field of requiredFields) {
      if (!(field in parsed)) {
        return { error: `Invalid game state: missing required field '${field}'` };
      }
    }

    // Validate grid dimensions and cell values
    const oceanValidation = this.validateOceanGrid(parsed.player1OceanGrid, 'player1OceanGrid');
    if (oceanValidation) return { error: oceanValidation };

    const ocean2Validation = this.validateOceanGrid(parsed.player2OceanGrid, 'player2OceanGrid');
    if (ocean2Validation) return { error: ocean2Validation };

    const trackingValidation = this.validateTrackingGrid(parsed.player1TrackingGrid, 'player1TrackingGrid');
    if (trackingValidation) return { error: trackingValidation };

    const tracking2Validation = this.validateTrackingGrid(parsed.player2TrackingGrid, 'player2TrackingGrid');
    if (tracking2Validation) return { error: tracking2Validation };

    // Validate fleet arrays
    if (!Array.isArray(parsed.player1Fleet)) {
      return { error: "Invalid game state: 'player1Fleet' must be an array" };
    }
    if (!Array.isArray(parsed.player2Fleet)) {
      return { error: "Invalid game state: 'player2Fleet' must be an array" };
    }

    for (const fleet of [parsed.player1Fleet, parsed.player2Fleet]) {
      for (const placement of fleet) {
        const placementErr = this.validateShipPlacement(placement);
        if (placementErr) return { error: placementErr };
      }
    }

    // Validate fleet status objects
    const fs1Err = this.validateFleetStatus(parsed.player1FleetStatus, 'player1FleetStatus');
    if (fs1Err) return { error: fs1Err };
    const fs2Err = this.validateFleetStatus(parsed.player2FleetStatus, 'player2FleetStatus');
    if (fs2Err) return { error: fs2Err };

    // Validate gamePhase
    const validPhases: GamePhase[] = ['placement', 'attack', 'completed'];
    if (!validPhases.includes(parsed.gamePhase)) {
      return { error: `Invalid game state: 'gamePhase' must be one of ${validPhases.join(', ')}` };
    }

    // Validate currentTurn
    const validPlayers: PlayerSide[] = ['player1', 'player2'];
    if (!validPlayers.includes(parsed.currentTurn)) {
      return { error: "Invalid game state: 'currentTurn' must be 'player1' or 'player2'" };
    }

    // Validate attackHistory
    if (!Array.isArray(parsed.attackHistory)) {
      return { error: "Invalid game state: 'attackHistory' must be an array" };
    }
    for (const record of parsed.attackHistory) {
      const recordErr = this.validateAttackRecord(record);
      if (recordErr) return { error: recordErr };
    }

    // Validate gameStatus
    const statusErr = this.validateGameStatus(parsed.gameStatus);
    if (statusErr) return { error: statusErr };

    // Validate boolean flags
    if (typeof parsed.player1Ready !== 'boolean') {
      return { error: "Invalid game state: 'player1Ready' must be a boolean" };
    }
    if (typeof parsed.player2Ready !== 'boolean') {
      return { error: "Invalid game state: 'player2Ready' must be a boolean" };
    }

    return parsed as BattleshipGameState;
  }

  /**
   * Restores the engine's internal state from a BattleshipGameState object.
   * Requirements: 5.2
   */
  setGameState(state: BattleshipGameState): void {
    this.player1OceanGrid = state.player1OceanGrid.map(row => [...row]);
    this.player2OceanGrid = state.player2OceanGrid.map(row => [...row]);
    this.player1TrackingGrid = state.player1TrackingGrid.map(row => [...row]);
    this.player2TrackingGrid = state.player2TrackingGrid.map(row => [...row]);

    this.player1Fleet = state.player1Fleet.map(p => ({ ...p, startCell: { ...p.startCell } }));
    this.player2Fleet = state.player2Fleet.map(p => ({ ...p, startCell: { ...p.startCell } }));

    this.gamePhase = state.gamePhase;
    this.currentTurn = state.currentTurn;
    this.attackHistory = state.attackHistory.map(r => ({ ...r, coordinate: { ...r.coordinate } }));
    this.gameStatus = { ...state.gameStatus } as BattleshipGameStatus;

    this.player1Ready = state.player1Ready;
    this.player2Ready = state.player2Ready;
  }

  // ==========================================================================
  // Deserialization Validation Helpers
  // ==========================================================================

  private validateOceanGrid(grid: any, fieldName: string): string | null {
    if (!Array.isArray(grid) || grid.length !== GRID_ROWS) {
      return `Invalid game state: '${fieldName}' must be a ${GRID_ROWS}x${GRID_COLS} array`;
    }
    for (let r = 0; r < GRID_ROWS; r++) {
      if (!Array.isArray(grid[r]) || grid[r].length !== GRID_COLS) {
        return `Invalid game state: '${fieldName}' row ${r} must have ${GRID_COLS} columns`;
      }
      for (let c = 0; c < GRID_COLS; c++) {
        if (grid[r][c] !== 'empty' && grid[r][c] !== 'ship') {
          return `Invalid game state: '${fieldName}[${r}][${c}]' must be 'empty' or 'ship'`;
        }
      }
    }
    return null;
  }

  private validateTrackingGrid(grid: any, fieldName: string): string | null {
    if (!Array.isArray(grid) || grid.length !== GRID_ROWS) {
      return `Invalid game state: '${fieldName}' must be a ${GRID_ROWS}x${GRID_COLS} array`;
    }
    for (let r = 0; r < GRID_ROWS; r++) {
      if (!Array.isArray(grid[r]) || grid[r].length !== GRID_COLS) {
        return `Invalid game state: '${fieldName}' row ${r} must have ${GRID_COLS} columns`;
      }
      for (let c = 0; c < GRID_COLS; c++) {
        const val = grid[r][c];
        if (val !== 'unknown' && val !== 'hit' && val !== 'miss' && val !== 'sunk') {
          return `Invalid game state: '${fieldName}[${r}][${c}]' must be 'unknown', 'hit', 'miss', or 'sunk'`;
        }
      }
    }
    return null;
  }

  private validateShipPlacement(placement: any): string | null {
    if (typeof placement !== 'object' || placement === null) {
      return 'Invalid game state: ship placement must be an object';
    }
    const validShipTypes: ShipType[] = ['carrier', 'battleship', 'cruiser', 'submarine', 'destroyer'];
    if (!validShipTypes.includes(placement.shipType)) {
      return `Invalid game state: invalid ship type '${placement.shipType}'`;
    }
    if (typeof placement.startCell !== 'object' || placement.startCell === null) {
      return 'Invalid game state: ship placement startCell must be an object';
    }
    if (typeof placement.startCell.row !== 'number' || typeof placement.startCell.col !== 'number') {
      return 'Invalid game state: ship placement startCell row and col must be numbers';
    }
    if (placement.startCell.row < 0 || placement.startCell.row >= GRID_ROWS ||
        placement.startCell.col < 0 || placement.startCell.col >= GRID_COLS) {
      return 'Invalid game state: ship placement startCell out of bounds';
    }
    if (placement.orientation !== 'horizontal' && placement.orientation !== 'vertical') {
      return "Invalid game state: ship placement orientation must be 'horizontal' or 'vertical'";
    }
    return null;
  }

  private validateFleetStatus(status: any, fieldName: string): string | null {
    if (typeof status !== 'object' || status === null) {
      return `Invalid game state: '${fieldName}' must be an object`;
    }
    if (!Array.isArray(status.ships)) {
      return `Invalid game state: '${fieldName}.ships' must be an array`;
    }
    if (typeof status.totalShips !== 'number') {
      return `Invalid game state: '${fieldName}.totalShips' must be a number`;
    }
    if (typeof status.shipsAfloat !== 'number') {
      return `Invalid game state: '${fieldName}.shipsAfloat' must be a number`;
    }
    if (typeof status.shipsSunk !== 'number') {
      return `Invalid game state: '${fieldName}.shipsSunk' must be a number`;
    }
    return null;
  }

  private validateAttackRecord(record: any): string | null {
    if (typeof record !== 'object' || record === null) {
      return 'Invalid game state: attack record must be an object';
    }
    if (typeof record.moveNumber !== 'number' || record.moveNumber < 1) {
      return 'Invalid game state: attack record moveNumber must be a positive number';
    }
    if (record.attacker !== 'player1' && record.attacker !== 'player2') {
      return "Invalid game state: attack record attacker must be 'player1' or 'player2'";
    }
    if (typeof record.coordinate !== 'object' || record.coordinate === null) {
      return 'Invalid game state: attack record coordinate must be an object';
    }
    if (typeof record.coordinate.row !== 'number' || typeof record.coordinate.col !== 'number') {
      return 'Invalid game state: attack record coordinate row and col must be numbers';
    }
    if (record.coordinate.row < 0 || record.coordinate.row >= GRID_ROWS ||
        record.coordinate.col < 0 || record.coordinate.col >= GRID_COLS) {
      return 'Invalid game state: attack record coordinate out of bounds';
    }
    const validResults = ['hit', 'miss', 'sunk'];
    if (!validResults.includes(record.result)) {
      return `Invalid game state: attack record result must be one of ${validResults.join(', ')}`;
    }
    if (typeof record.timestamp !== 'number') {
      return 'Invalid game state: attack record timestamp must be a number';
    }
    return null;
  }

  private validateGameStatus(status: any): string | null {
    if (typeof status !== 'object' || status === null) {
      return "Invalid game state: 'gameStatus' must be an object";
    }
    const validTypes = ['placement', 'active', 'win', 'resignation'];
    if (!validTypes.includes(status.type)) {
      return `Invalid game state: 'gameStatus.type' must be one of ${validTypes.join(', ')}`;
    }
    if ((status.type === 'win' || status.type === 'resignation') &&
        status.winner !== 'player1' && status.winner !== 'player2') {
      return `Invalid game state: 'gameStatus.winner' must be 'player1' or 'player2' when type is '${status.type}'`;
    }
    return null;
  }

  // ==========================================================================
  // Game Statistics (Task 3.2)
  // ==========================================================================

  /**
   * Calculates game statistics on completion.
   * Requirements: 9.5
   */
  getGameStatistics(): {
    totalAttacks: number;
    player1Attacks: number;
    player2Attacks: number;
    player1Hits: number;
    player2Hits: number;
    player1HitPercentage: number;
    player2HitPercentage: number;
    player1ShipsSunk: number;
    player2ShipsSunk: number;
  } {
    const player1Attacks = this.attackHistory.filter(a => a.attacker === 'player1');
    const player2Attacks = this.attackHistory.filter(a => a.attacker === 'player2');

    const player1Hits = player1Attacks.filter(
      a => a.result === 'hit' || a.result === 'sunk'
    ).length;
    const player2Hits = player2Attacks.filter(
      a => a.result === 'hit' || a.result === 'sunk'
    ).length;

    const player1FleetStatus = this.getFleetStatus('player1');
    const player2FleetStatus = this.getFleetStatus('player2');

    return {
      totalAttacks: this.attackHistory.length,
      player1Attacks: player1Attacks.length,
      player2Attacks: player2Attacks.length,
      player1Hits,
      player2Hits,
      player1HitPercentage:
        player1Attacks.length > 0
          ? (player1Hits / player1Attacks.length) * 100
          : 0,
      player2HitPercentage:
        player2Attacks.length > 0
          ? (player2Hits / player2Attacks.length) * 100
          : 0,
      player1ShipsSunk: player2FleetStatus.shipsSunk, // player1 sinks player2's ships
      player2ShipsSunk: player1FleetStatus.shipsSunk, // player2 sinks player1's ships
    };
  }
}
